import { environment } from '@/environments/environment';
import { SiteMapNode } from '@/models';
import { RouteData } from '@/models/route-data';
import { Data } from '@angular/router';
import { createSelector } from '@ngrx/store';
import { selectSiteMapState } from '../reducers';
import {
  selectChildRouterLinks,
  selectRouteData,
  selectUrl,
} from './router.selector';

/**
 * Recursively finds the matching sitemap node for the currentUrl.
 * Returns the first matching node (deepest in hierarchy), or null if none found.
 */
function findMatchingSitemapNode(
  currentUrl: string,
  node: SiteMapNode,
): { parent: SiteMapNode | null; selectedNode: SiteMapNode } | null {
  const baseUrl = currentUrl.split('?')[0];

  // Check if this node matches
  if (baseUrl === node.route || baseUrl.startsWith(node.route + '/')) {
    // Try to find a deeper match first
    if (node.children) {
      for (const child of node.children) {
        const match = findMatchingSitemapNode(currentUrl, child);
        if (match) {
          return {
            parent: match.parent ?? node,
            selectedNode: match.selectedNode,
          };
        }
      }
    }
    // If no deeper match, this node is the match
    return { parent: null, selectedNode: node };
  }

  // Recurse into children if present (in case siblings might match)
  if (node.children) {
    for (const child of node.children) {
      const match = findMatchingSitemapNode(currentUrl, child);
      if (match) {
        return {
          parent: match.parent ?? node,
          selectedNode: match.selectedNode,
        };
      }
    }
  }

  return null;
}

function fetchSiteMapNodeByKey(
  siteMapNodes: SiteMapNode[],
  key: string,
): SiteMapNode | null {
  for (const node of siteMapNodes) {
    if (node.key === key) {
      return node;
    }
    if (node.children) {
      const childResult = fetchSiteMapNodeByKey(node.children, key);
      if (childResult) {
        return childResult;
      }
    }
  }
  return null;
}

export const selectCurrentSiteMap = createSelector(
  selectSiteMapState,
  (siteMapState): SiteMapNode[] =>
    siteMapState.siteMap.filter(
      (n) => !n.key || !environment.disabledSitemapNodes.includes(n.key),
    ),
);

export const selectIsSiteMapLoading = createSelector(
  selectSiteMapState,
  (siteMapState) => siteMapState.loading,
);

export const selectSelectedSiteMapNodeInfo = createSelector(
  selectSiteMapState,
  selectRouteData,
  selectUrl,
  (
    siteMapState,
    routeData: RouteData & Data,
    url,
  ): { parent: SiteMapNode; selectedNode: SiteMapNode } | null => {
    let node: { parent: SiteMapNode; selectedNode: SiteMapNode } | null = null;

    let selectedNode: SiteMapNode | null = null;
    let sideNavSelectedNode: SiteMapNode | null = null;
    if (routeData.siteMapNodeKey) {
      selectedNode = fetchSiteMapNodeByKey(
        siteMapState.siteMap,
        routeData.siteMapNodeKey,
      );
    }
    if (routeData.selectedSideNavSiteMapNodeKey) {
      sideNavSelectedNode = fetchSiteMapNodeByKey(
        siteMapState.siteMap,
        routeData.selectedSideNavSiteMapNodeKey,
      );
    }

    if (sideNavSelectedNode) {
      node = {
        parent: sideNavSelectedNode,
        selectedNode: selectedNode || sideNavSelectedNode,
      };
    }

    if (!node) {
      for (const currentNode of siteMapState.siteMap) {
        const match = findMatchingSitemapNode(url, currentNode);
        if (match) {
          node = {
            parent: match.parent ?? currentNode,
            selectedNode: match.selectedNode,
          };
        }
      }
    }

    return node;
  },
);

export const selectSelectedSiteMapNode = createSelector(
  selectSelectedSiteMapNodeInfo,
  (nodeInfo): SiteMapNode | null => nodeInfo?.selectedNode || null,
);

export const selectShowChildNavigation = createSelector(
  selectSelectedSiteMapNodeInfo,
  selectChildRouterLinks,
  (nodeInfo, childRouterLinks): boolean => {
    const selectedNode = nodeInfo?.selectedNode;
    const parentNode = nodeInfo?.parent;

    const hasChildNavDataFromSitemapData =
      !!selectedNode?.children?.some((c) => c.displayInSubNavigation) ||
      (!!parentNode &&
        !!parentNode.children &&
        !!selectedNode &&
        selectedNode.displayInSubNavigation &&
        parentNode.children?.some((c) => c.displayInSubNavigation));

    return hasChildNavDataFromSitemapData || childRouterLinks?.length > 0;
  },
);

export const selectChildNavigationNodes = createSelector(
  selectSelectedSiteMapNodeInfo,
  selectChildRouterLinks,
  (selectedNode, childRouterLinks): SiteMapNode[] | null => {
    const childNavNodes =
      selectedNode?.selectedNode.children?.filter(
        (c) => c.displayInSubNavigation,
      ) || [];

    const navNodes: SiteMapNode[] = [];

    if (childNavNodes.length > 0) {
      childNavNodes.forEach((node) => navNodes.push(node));
    } else if (selectedNode?.parent) {
      const parentChildNodes =
        selectedNode.parent.children?.filter((c) => c.displayInSubNavigation) ||
        ([] as SiteMapNode[]);
      parentChildNodes.forEach((node) => navNodes.push(node));
    }

    if (childRouterLinks?.length > 0) {
      childRouterLinks
        .map(
          (link, index) =>
            ({
              ordinalPosition: index,
              displayName: link.label,
              route: link.route,
            }) as SiteMapNode,
        )
        .forEach((node) => navNodes.push(node));
    }

    return navNodes
      .filter(
        (n) => !n.key || !environment.disabledSitemapNodes.includes(n.key),
      )
      .sort((a, b) => a.ordinalPosition - b.ordinalPosition);
  },
);
